(()=>{
  'use strict';

  const STORAGE_KEY='muaazon_recent_searches_v2';
  const CACHE=new Map();

  const clamp=(value,min,max)=>Math.min(max,Math.max(min,value));
  const recentLimit=()=>clamp(Number(document.documentElement.dataset.qxRecentSearchLimit||5)||5,1,10);

  function safeRecentRead(){
    try{
      const parsed=JSON.parse(localStorage.getItem(STORAGE_KEY)||'[]');
      return Array.isArray(parsed)?parsed.filter(item=>typeof item==='string'&&item.trim()).slice(0,recentLimit()):[];
    }catch(_error){return [];}
  }

  function safeRecentWrite(items){
    try{localStorage.setItem(STORAGE_KEY,JSON.stringify(items.slice(0,recentLimit())));}catch(_error){}
  }

  function remember(term){
    const clean=String(term||'').trim();
    if(!clean)return;
    const next=[clean,...safeRecentRead().filter(item=>item.toLowerCase()!==clean.toLowerCase())];
    safeRecentWrite(next);
  }

  function markReady(scope=document){
    scope.querySelectorAll('[data-search-drawer],[data-predictive-search-input],[data-predictive-search-results],[data-qx-search-recent]').forEach(node=>{
      node.dataset.qxReady='true';
    });
  }

  function initDrawer(drawer){
    if(drawer.dataset.qxSearchInitialized==='true')return;
    const input=drawer.querySelector('[data-predictive-search-input]');
    const results=drawer.querySelector('[data-predictive-search-results]');
    if(!input||!results)return;

    drawer.dataset.qxSearchInitialized='true';
    drawer.dataset.qxReady='true';
    input.dataset.qxReady='true';
    results.dataset.qxReady='true';

    const form=input.closest('form');
    const status=drawer.querySelector('[data-qx-search-state-status]');
    const recent=drawer.querySelector('[data-qx-search-recent]');
    const recentList=drawer.querySelector('[data-qx-search-recent-list]');
    const discoveryHTML=results.innerHTML;
    let timer=0;
    let aborter=null;
    let activeIndex=-1;

    const announce=message=>{if(status)status.textContent=message||'';};
    const options=()=>Array.from(results.querySelectorAll('[data-predictive-option]'));

    function normalizeOptions(){
      options().forEach((option,index)=>{
        if(!option.id)option.id=`QXPredictiveOption-${drawer.id||'drawer'}-${index+1}`;
        option.setAttribute('aria-selected','false');
      });
    }

    function setActive(index){
      const list=options();
      list.forEach(option=>{
        option.classList.remove('is-active');
        option.setAttribute('aria-selected','false');
      });
      if(!list.length){
        activeIndex=-1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      activeIndex=(index+list.length)%list.length;
      const active=list[activeIndex];
      active.classList.add('is-active');
      active.setAttribute('aria-selected','true');
      input.setAttribute('aria-activedescendant',active.id);
      active.scrollIntoView({block:'nearest'});
    }

    function restoreDiscovery(){
      if(aborter){aborter.abort();aborter=null;}
      clearTimeout(timer);
      results.innerHTML=discoveryHTML;
      activeIndex=-1;
      input.setAttribute('aria-expanded','false');
      input.removeAttribute('aria-activedescendant');
      renderRecent();
      announce('');
    }

    function renderRecent(){
      const host=results.querySelector('[data-qx-search-recent-list]')||recentList;
      const wrap=results.querySelector('[data-qx-search-recent]')||recent;
      if(!host||!wrap)return;
      const items=safeRecentRead();
      host.replaceChildren();
      items.forEach(term=>{
        const link=document.createElement('a');
        const searchUrl=form?.action||'/search';
        link.href=`${searchUrl}?q=${encodeURIComponent(term)}`;
        link.textContent=term;
        link.className='qx-search-recent__link';
        host.appendChild(link);
      });
      wrap.hidden=!items.length;
    }

    function sectionMarkup(text){
      const doc=new DOMParser().parseFromString(text,'text/html');
      const section=doc.querySelector('#shopify-section-predictive-search');
      if(section)return section.innerHTML;
      return doc.body.innerHTML||text;
    }

    async function request(term){
      if(CACHE.has(term)){
        results.innerHTML=CACHE.get(term);
        normalizeOptions();
        input.setAttribute('aria-expanded',options().length?'true':'false');
        announce(`Search suggestions updated for ${term}.`);
        return;
      }

      if(aborter)aborter.abort();
      aborter=new AbortController();
      drawer.classList.add('is-searching');
      announce(`Searching for ${term}.`);
      try{
        const url=`/search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article,query&resources[limit]=6&section_id=predictive-search`;
        const response=await fetch(url,{signal:aborter.signal,headers:{Accept:'text/html'}});
        if(!response.ok)throw new Error(`Predictive search failed: ${response.status}`);
        const html=sectionMarkup(await response.text());
        CACHE.set(term,html);
        if(input.value.trim()!==term)return;
        results.innerHTML=html;
        normalizeOptions();
        input.setAttribute('aria-expanded',options().length?'true':'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex=-1;
        announce(`Search suggestions updated for ${term}.`);
      }catch(error){
        if(error.name==='AbortError')return;
        input.setAttribute('aria-expanded','false');
        input.removeAttribute('aria-activedescendant');
        announce('Search suggestions are temporarily unavailable. Press Enter to search.');
      }finally{
        drawer.classList.remove('is-searching');
      }
    }

    function queue(){
      clearTimeout(timer);
      const term=input.value.trim();
      activeIndex=-1;
      input.removeAttribute('aria-activedescendant');
      if(term.length<2){restoreDiscovery();return;}
      timer=window.setTimeout(()=>request(term),180);
    }

    input.addEventListener('input',queue);
    input.addEventListener('keydown',event=>{
      const list=options();
      if(event.key==='ArrowDown'&&list.length){event.preventDefault();setActive(activeIndex+1);return;}
      if(event.key==='ArrowUp'&&list.length){event.preventDefault();setActive(activeIndex-1);return;}
      if(event.key==='Home'&&list.length&&input.getAttribute('aria-expanded')==='true'){event.preventDefault();setActive(0);return;}
      if(event.key==='End'&&list.length&&input.getAttribute('aria-expanded')==='true'){event.preventDefault();setActive(list.length-1);return;}
      if(event.key==='Enter'&&activeIndex>=0&&list[activeIndex]){event.preventDefault();list[activeIndex].click();return;}
      if(event.key==='Escape'){
        event.preventDefault();
        if(aborter)aborter.abort();
        const close=drawer.querySelector('[data-search-close]');
        if(close)close.click();
        else{
          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden','true');
        }
        input.setAttribute('aria-expanded','false');
        input.removeAttribute('aria-activedescendant');
      }
    });

    results.addEventListener('mousemove',event=>{
      const option=event.target.closest('[data-predictive-option]');
      if(!option)return;
      const list=options();
      const index=list.indexOf(option);
      if(index>=0)setActive(index);
    });

    results.addEventListener('click',event=>{
      const link=event.target.closest('a[href]');
      if(link&&input.value.trim())remember(input.value);
      const clear=event.target.closest('[data-qx-search-recent-clear]');
      if(clear){
        event.preventDefault();
        try{localStorage.removeItem(STORAGE_KEY);}catch(_error){}
        renderRecent();
        announce('Recent searches cleared.');
      }
    });

    if(form)form.addEventListener('submit',()=>remember(input.value));

    renderRecent();
    normalizeOptions();
  }

  function init(scope=document){
    markReady(scope);
    scope.querySelectorAll('[data-search-drawer]').forEach(initDrawer);
  }

  init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
})();
